import moment from "moment";
import type { ChipType } from "../components/Chips/Chips";
import type { AttributeSchema } from "../types/types.PIM";
import type { NumericFilterCriterion } from "../types/types";
import { decodeNumericFilterCriterion } from "../components/Filters/filterutils";
import styled from "styled-components";

export const SearchHighlight = styled.span<{ $active: boolean }>`
  ${({ $active, theme }) =>
    $active &&
    `
    background-color: ${theme.resolve("color_cellSearchHighlight_bg")};
     border-radius: ${theme.resolve("radius_sm")};
    padding: ${theme.resolve("spacing_xs")} ${theme.resolve("spacing_sm")};
    margin: -${theme.resolve("spacing_xs")} -${theme.resolve("spacing_sm")};
    display: inline-block;
  `}
`;

/**
 * Internal sentinel used to represent the "Unassigned" filter option.
 * Stored as the URL param value so restoration survives page refreshes.
 */
export const UNASSIGNED_FILTER_ID = "__unassigned__";

/** Converts a collection/column name into a safe, lowercase URL param key. */
export function toUrlKey(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[:/?&=]/g, "");
}

/**
 * Order-independent equality for query strings. Multiple collection tables on
 * the same page each rewrite their own `*_f_*` params; comparing `toString()`
 * directly oscillates on key order and freezes the page.
 * Uses URLSearchParams serialization so values containing `&` / `=` stay unique.
 */
export function areSearchParamsEqual(
  a: string | URLSearchParams,
  b: string | URLSearchParams
): boolean {
  const normalize = (input: string | URLSearchParams): string => {
    const params =
      typeof input === "string" ? new URLSearchParams(input) : input;
    return new URLSearchParams(
      Array.from(params.entries()).sort(([left], [right]) =>
        left.localeCompare(right)
      )
    ).toString();
  };
  return normalize(a) === normalize(b);
}

/** Flattens any cell value into a single searchable string. */
export function valueToSearchString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) return value.map(valueToSearchString).join(" ");
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    // "display_text" covers link attributes ({ display_text, url }).
    if ("display_text" in obj) return String(obj.display_text ?? "");
    // User attribute: { firstname, lastname, ... } – produce "First Last".
    // Checked before the generic "name"/"value" fallbacks because User objects
    // do not carry those properties and would otherwise fall through to
    // Object.values(), leaking UUIDs, emails, etc.
    if ("firstname" in obj || "lastname" in obj) {
      return [String(obj.firstname ?? ""), String(obj.lastname ?? "")]
        .filter(Boolean)
        .join(" ")
        .trim();
    }
    // "name" is checked before "value" because product_reference / object_reference
    // objects carry both: { name: "Display Name", value: "uuid", ... }. We want
    // the human-readable name, not the internal ID.
    if ("name" in obj) return String(obj.name ?? "");
    if ("value" in obj) return String(obj.value ?? "");
    return Object.values(obj).map(valueToSearchString).join(" ");
  }
  return String(value);
}

/**
 * Returns every distinct atomic string contained in a cell value.
 * Arrays are unwrapped so each element becomes its own entry, giving
 * multi-select attributes one chip per selected option in the filter UI.
 */
export function valueToFilterStrings(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (typeof value === "string") return value ? [value] : [];
  if (typeof value === "number") {
    return [String(value)];
  }
  if (typeof value === "boolean") {
    return [String(value)?.charAt(0).toUpperCase() + String(value)?.slice(1)];
  }
  if (Array.isArray(value)) {
    return value.flatMap(valueToFilterStrings);
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    // "display_text" covers link attributes ({ display_text, url }).
    if ("display_text" in obj) {
      const s = String(obj.display_text ?? "");
      return s ? [s] : [];
    }
    // User attribute: { firstname, lastname, ... } – one chip per unique user
    // showing "First Last". Checked before the generic fallbacks so UUIDs,
    // emails, etc. are never surfaced as separate chips.
    if ("firstname" in obj || "lastname" in obj) {
      const s = [String(obj.firstname ?? ""), String(obj.lastname ?? "")]
        .filter(Boolean)
        .join(" ")
        .trim();
      return s ? [s] : [];
    }
    // "name" before "value": product_reference / object_reference objects have
    // both { name: "Display Name", value: "uuid" }. Prefer the display name.
    if ("name" in obj) {
      const s = String(obj.name ?? "");
      return s ? [s] : [];
    }
    if ("value" in obj) {
      const s = String(obj.value ?? "");
      return s ? [s] : [];
    }
    return Object.values(obj).flatMap(valueToFilterStrings);
  }
  return [String(value)];
}

/**
 * Returns the human-readable display strings for a cell value given the
 * column schema. For date columns the ISO value is formatted to match the
 * display format (MMM DD, YYYY) so searching/filtering by display text works.
 * For all other types it delegates to valueToFilterStrings.
 */
export function valueToDisplayStrings(
  col: AttributeSchema,
  value: unknown
): string[] {
  if (col.input_type === "date") {
    const raw = valueToSearchString(value);
    if (!raw) return [];
    const formatted = moment(raw, [
      "YYYY-MM-DD",
      "MMM DD, YYYY",
      moment.ISO_8601,
    ]).isValid()
      ? moment(raw, ["YYYY-MM-DD", "MMM DD, YYYY", moment.ISO_8601]).format(
          "MMM DD, YYYY"
        )
      : raw;
    const results = new Set([raw, formatted]);
    return Array.from(results).filter(Boolean);
  }
  return valueToFilterStrings(value);
}

/**
 * Returns true if a cell value matches the given set of selected filter chips.
 * Handles the special "Unassigned" chip (UNASSIGNED_FILTER_ID) which matches
 * rows that have no value for that column.
 */
export function matchesFilter(
  col: AttributeSchema,
  cellValue: unknown,
  selectedChips: ChipType[]
): boolean {
  if (selectedChips.length === 0) return true;

  // AP-1016 (Advanced Filters, Part 1): numeric columns are filtered by an
  // operator + value(s) criterion — encoded as the sole chip's `name`, see
  // the numeric branch in ProductCollectionSchemaTable.tsx's column header —
  // rather than chip-equality against display strings.
  if (col.input_type === "numeric") {
    const criterion = decodeNumericFilterCriterion(selectedChips[0]?.name);
    return criterion ? matchesNumericFilter(cellValue, criterion) : true;
  }

  const cellStrings = valueToDisplayStrings(col, cellValue);
  const includeUnassigned = selectedChips.some(
    (c) => c.id === UNASSIGNED_FILTER_ID
  );
  if (includeUnassigned && cellStrings.length === 0) return true;
  const selectedValues = new Set(
    selectedChips
      .filter((c) => c.id !== UNASSIGNED_FILTER_ID)
      .map((c) => c.name)
  );
  return cellStrings.some((s) => selectedValues.has(s.trim()));
}

/**
 * AP-1016 (Advanced Filters, Part 1) — parses a collection cell value into a
 * finite number, or null when it isn't one. Numeric-attribute values are
 * never parsed/stripped/converted (e.g. "12 kg" is not coerced to 12) — a
 * row whose value isn't cleanly numeric is excluded from numeric matching,
 * per spec, rather than guessed at.
 */
export function parseNumericCellValue(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * AP-1016 (Advanced Filters) — matches a collection cell against a numeric
 * filter criterion, per the matching semantics the backend's FE spec
 * defers to the FE for on this surface (§5.3: "matching semantics are
 * yours to implement") and the Confluence spec's Operators & Match Rules.
 * Collection numeric columns store a single scalar value per row in Part 1
 * (Min/Max/Is range mapping is Part 2), so "is"/"is_exactly" both reduce
 * to equality and "between"/"overlaps" both reduce to inclusive range
 * containment — the operators only diverge once a row can hold a stored
 * *range* (Part 2). A range criterion's Min/Max are independently
 * optional — an unfilled bound is open-ended, so "only Min" behaves as
 * "at least" and "only Max" as "at most". A record with no value is never
 * matched, by any operator.
 */
export function matchesNumericFilter(
  cellValue: unknown,
  criterion: NumericFilterCriterion
): boolean {
  const cellNumber = parseNumericCellValue(cellValue);
  if (cellNumber === null) return false;

  switch (criterion.operator) {
    case "is":
    case "is_exactly":
      return cellNumber === criterion.value;
    case "between":
    case "overlaps": {
      const { min, max } = criterion;
      if (min !== undefined && cellNumber < min) return false;
      if (max !== undefined && cellNumber > max) return false;
      return true;
    }
  }
}

/** A slice of text that either does or doesn't match the search query. */
export type HighlightSegment = { text: string; matched: boolean };

/**
 * Splits text into segments around the first match of searchQuery, so callers
 * can render the matched segment as bold text using React elements. Returns
 * plain data (not HTML) so callers never need dangerouslySetInnerHTML on
 * cell values, which may contain arbitrary tenant-entered text.
 */
export function getHighlightSegments(
  text: string,
  searchQuery: string
): HighlightSegment[] {
  if (searchQuery.length === 0) return [{ text, matched: false }];
  const lowerText = text.toLowerCase();
  const lowerQuery = searchQuery.toLowerCase();
  const start = lowerText.indexOf(lowerQuery);
  if (start === -1) return [{ text, matched: false }];
  const end = start + searchQuery.length;
  const segments: HighlightSegment[] = [];
  if (start > 0) {
    segments.push({ text: text.substring(0, start), matched: false });
  }
  segments.push({ text: text.substring(start, end), matched: true });
  if (end < text.length) {
    segments.push({ text: text.substring(end), matched: false });
  }
  return segments;
}
